// pictionary/games/BuildWordGame.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardMedia, 
  Button, 
  Paper, 
  Grow, 
  Fade, 
  useTheme 
} from '@mui/material';
import { useGame } from '../../../context/GameContext';
import { green, red, grey, blue } from '@mui/material/colors';
import ReplayIcon from '@mui/icons-material/Replay';

// --- LetterSlot Component - Enhanced 3D effects ---
const LetterSlot = ({ letter, filled, onRemove, index, answerStatus, isAnswered }) => {
  const theme = useTheme();
  const isCorrect = answerStatus === 'correct';
  const isIncorrect = answerStatus === 'incorrect';

  const slotStyles = {
    width: { xs: 60, sm: 70 }, // Slightly larger
    height: { xs: 60, sm: 70 },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 3,
    fontSize: { xs: '1.8rem', sm: '2.2rem' }, // Larger font
    fontWeight: 'bold',
    color: filled ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    border: '3px solid',
    borderColor: filled ? theme.palette.primary.dark : grey[300],
    bgcolor: filled ? theme.palette.primary.light : grey[100],
    cursor: filled && !isAnswered ? 'pointer' : 'default',
    transition: 'all 0.3s ease',
    position: 'relative',
    margin: '6px', // More margin
    
    // Duolingo-style 3D effects
    transform: filled ? 'translateY(-5px)' : 'translateY(0)',
    boxShadow: filled 
      ? '0 8px 16px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.08)'
      : '0 2px 4px rgba(0,0,0,0.1)',
    
    // Bottom shadow layer for 3D effect (only when filled)
    ...(filled && {
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 4,
        right: 4,
        height: 8,
        backgroundColor: isAnswered
          ? (isCorrect ? green[300] : red[300])
          : theme.palette.primary.dark,
        borderBottomLeftRadius: 3,
        borderBottomRightRadius: 3,
        zIndex: -1,
      }
    }),

    // Feedback Styling (only applies when answered)
    ...(isAnswered && filled && {
      borderColor: isCorrect ? green[500] : red[500],
      bgcolor: isCorrect ? green[100] : red[100],
      color: isCorrect ? green[900] : red[900],
    }),

    // Pop Animation when filled
    ...(filled && !isAnswered && {
      animation: 'pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      '@keyframes pop': {
        '0%': { transform: 'scale(0.8) translateY(0)', opacity: 0.8 },
        '50%': { transform: 'scale(1.1) translateY(-7px)' },
        '100%': { transform: 'scale(1) translateY(-5px)', opacity: 1 },
      },
    }),
    
    // Hover effect when filled and not answered
    ...(filled && !isAnswered && {
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.2), 0 6px 10px rgba(0,0,0,0.12)',
        borderColor: red[400], // Indicate it can be removed on hover
      }
    }),
  };

  return (
    <Paper
      elevation={filled ? 4 : 1}
      onClick={filled && !isAnswered ? onRemove : undefined}
      sx={slotStyles}
    >
      {letter}
    </Paper>
  );
};

// --- LetterOption Component - Enhanced 3D effects ---
const LetterOption = ({ letter, index, onSelect, used, isAnswered }) => {
  const theme = useTheme();
  const isDisabled = used || isAnswered;

  return (
    <Box
      sx={{
        position: 'relative',
        margin: '6px',
        // Add bottom "layer" for 3D effect when not disabled
        ...(!isDisabled && {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0, // Distance from main letter
            left: 4,
            right: 4,
            height: 8,
            backgroundColor: theme.palette.primary.dark,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            zIndex: 0,
            opacity: 0.7,
          }
        })
      }}
    >
      <Paper
        elevation={isDisabled ? 1 : 4}
        onClick={!isDisabled ? onSelect : undefined}
        sx={{
          width: { xs: 60, sm: 70 }, // Larger
          height: { xs: 60, sm: 70 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 3,
          fontSize: { xs: '1.8rem', sm: '2.2rem' }, // Larger font
          fontWeight: 'bold',
          color: theme.palette.primary.contrastText,
          bgcolor: theme.palette.primary.main,
          cursor: !isDisabled ? 'pointer' : 'default',
          opacity: isDisabled ? 0.4 : 1, // More fade out
          position: 'relative',
          boxShadow: isDisabled 
            ? 'none' 
            : '0 8px 16px rgba(0, 123, 255, 0.25), 0 4px 8px rgba(0, 123, 255, 0.15)',
          transform: isDisabled ? 'none' : 'translateY(-5px)', // 3D lift effect
          transition: 'all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          
          // Enhanced hover effect
          '&:hover': !isDisabled ? {
            transform: 'translateY(-8px) scale(1.05)',
            boxShadow: '0 12px 24px rgba(0, 123, 255, 0.3), 0 8px 12px rgba(0, 123, 255, 0.2)',
            bgcolor: theme.palette.primary.dark,
          } : {},
        }}
      >
        {letter}
        {/* Number badge */}
        {index < 9 && !isDisabled && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0, 0, 0, 0.6)', // Darker, more visible
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              p: '2px 6px',
              borderRadius: 1,
              lineHeight: 1,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)', // Subtle shadow for badge
            }}
          >
            {index + 1}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// --- BuildWordGame Component ---
const BuildWordGame = ({ word, onAnswerSubmit }) => {
  const { shuffleArray } = useGame();
  const theme = useTheme();
  const [slots, setSlots] = useState([]);
  const [letters, setLetters] = useState([]);
  const [usedIndices, setUsedIndices] = useState(new Set());
  const [answerStatus, setAnswerStatus] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);

  const wordIdRef = useRef('');
  const submitTimeoutRef = useRef(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  // Initialize / Reset game state when word changes
  useEffect(() => {
    if (!word) {
      setSlots([]);
      setLetters([]);
      setUsedIndices(new Set());
      setAnswerStatus(null);
      setIsAnswered(false);
      wordIdRef.current = '';
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
      return;
    }

    if (word.id !== wordIdRef.current) {
      console.log("Initializing BuildWordGame for:", word.word);
      const wordLetters = word.word.toUpperCase().split('');
      setSlots(Array(wordLetters.length).fill(''));
      const shuffledLetters = shuffleArray([...wordLetters]);
      setLetters(shuffledLetters);
      setUsedIndices(new Set());
      setAnswerStatus(null);
      setIsAnswered(false);
      wordIdRef.current = word.id;
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
    }
  }, [word, shuffleArray]);

  // Function to handle answer submission logic
  const submitAnswer = useCallback(() => {
    if (isAnswered || !word) return;

    const builtWord = slots.join('');
    const targetWord = word.word.toUpperCase();
    const isCorrect = builtWord === targetWord;

    setAnswerStatus(isCorrect ? 'correct' : 'incorrect');
    setIsAnswered(true);

    submitTimeoutRef.current = setTimeout(() => {
      console.log("Submitting answer via timeout:", isCorrect);
      onAnswerSubmit(isCorrect, targetWord);
    }, 1500);
  }, [slots, word, isAnswered, onAnswerSubmit]);

  // Handle selecting a letter option
  const handleLetterSelect = useCallback((letter, index) => {
    if (isAnswered) return;

    setSlots(prevSlots => {
      const emptySlotIndex = prevSlots.findIndex(slot => slot === '');
      if (emptySlotIndex === -1) return prevSlots;

      const newSlots = [...prevSlots];
      newSlots[emptySlotIndex] = letter;

      setUsedIndices(prev => new Set(prev).add(index));

      if (newSlots.every(slot => slot !== '')) {
        console.log("All slots filled, ready for submission.");
      }

      return newSlots;
    });
  }, [isAnswered]);

  // Handle removing a letter from a slot
  const handleSlotRemove = useCallback((slotIndex) => {
    if (isAnswered) return;

    setSlots(prevSlots => {
      const letter = prevSlots[slotIndex];
      if (!letter) return prevSlots;

      const newSlots = [...prevSlots];
      newSlots[slotIndex] = '';

      setUsedIndices(prevUsedIndices => {
        let indexToRemove = -1;
        for(let i = 0; i < letters.length; i++) {
          if(letters[i] === letter && prevUsedIndices.has(i)) {
            indexToRemove = i;
            break;
          }
        }

        if (indexToRemove !== -1) {
          const newUsedIndices = new Set(prevUsedIndices);
          newUsedIndices.delete(indexToRemove);
          return newUsedIndices;
        }
        return prevUsedIndices;
      });

      return newSlots;
    });
  }, [isAnswered, letters]);

  // Handle resetting the current word attempt
  const handleReset = useCallback(() => {
    if (isAnswered || !word) return;
    setSlots(Array(word.word.length).fill(''));
    setUsedIndices(new Set());
  }, [word, isAnswered]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isAnswered) {
        // Enter key for continuing after answering
        if (e.key === 'Enter') {
          // Parent component should handle this
        }
        return;
      }

      // Backspace: Remove last filled letter
      if (e.key === 'Backspace') {
        e.preventDefault();
        const lastFilledIndex = slots.map((slot, index) => slot !== '' ? index : -1).filter(index => index !== -1).pop();
        if (lastFilledIndex !== undefined) {
          handleSlotRemove(lastFilledIndex);
        }
        return;
      }

      // Enter: Submit answer if all slots are filled
      if (e.key === 'Enter' && slots.every(slot => slot !== '')) {
        submitAnswer();
        return;
      }

      // Number keys (1-9): Select corresponding letter if available
      const keyNum = parseInt(e.key, 10);
      if (!isNaN(keyNum) && keyNum >= 1 && keyNum <= letters.length) {
        const index = keyNum - 1;
        if (letters[index] && !usedIndices.has(index)) {
          handleLetterSelect(letters[index], index);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [slots, letters, usedIndices, isAnswered, handleLetterSelect, handleSlotRemove, submitAnswer]);

  if (!word) return null;

  // Check if all slots are filled
  const allSlotsFilled = slots.every(slot => slot !== '');

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        p: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        minHeight: 0,
        flexGrow: 1,
        position: 'relative',
        // Removed overflow: 'auto' and maxHeight constraints
      }}
    >
      {/* Top Section: Image - LARGER */}
      <Grow in={true} timeout={500}>
        <Card
          elevation={8} // Stronger shadow for 3D effect
          sx={{
            width: { xs: 220, sm: 280, md: 340 }, // Larger image
            height: { xs: 220, sm: 280, md: 340 },
            mb: { xs: 3, sm: 4 },
            cursor: 'default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            borderRadius: 3,
            overflow: 'hidden',
            flexShrink: 0,
            // Duolingo-style 3D effect
            boxShadow: '0 12px 24px rgba(0,0,0,0.2), 0 8px 12px rgba(0,0,0,0.1)',
            transform: 'translateY(-5px)',
            position: 'relative',
            '&::after': { // Bottom "layer" for 3D effect
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 4,
              right: 4,
              height: 8,
              backgroundColor: 'rgba(0,0,0,0.15)',
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
              zIndex: -1,
            }
          }}
        >
          <CardMedia
            component="img"
            image={word?.image}
            alt={`Image for ${word?.word}`}
            sx={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }}
          />
        </Card>
      </Grow>

      {/* Middle Section: Letter Slots with Reset Button on the right */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          justifyContent: 'center',
          gap: 2,
          width: '100%',
          mb: { xs: 3, sm: 4 },
          flexShrink: 0,
        }}
      >
        {/* Letter Slots Container */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 1, sm: 1.5 },
            padding: '8px',
            flexGrow: 0,
          }}
        >
          {slots.map((slot, index) => (
            <LetterSlot
              key={index}
              letter={slot}
              filled={slot !== ''}
              onRemove={() => handleSlotRemove(index)}
              index={index}
              answerStatus={answerStatus}
              isAnswered={isAnswered}
            />
          ))}
        </Box>

        {/* Reset Button - Now positioned to the right of slots */}
        <Fade in={!isAnswered && slots.some(s => s !== '')} timeout={300}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            alignSelf: { xs: 'center', sm: 'center' },
            padding: { xs: '0', sm: '8px 0' },
          }}>
            <Button
              variant="contained"
              onClick={handleReset}
              disabled={isAnswered || !slots.some(s => s !== '')}
              startIcon={<ReplayIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                p: '8px 16px',
                fontSize: '1rem',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease',
                height: { xs: 48, sm: 52 }, // Fixed height to align with slots
                whiteSpace: 'nowrap',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 2,
                  right: 2,
                  height: 4,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderBottomLeftRadius: 2,
                  borderBottomRightRadius: 2,
                  zIndex: -1,
                },
              }}
            >
              Reset
            </Button>
          </Box>
        </Fade>
      </Box>

      {/* Bottom Section: Letter Options - Improved layout */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 1 },
          p: { xs: 1, sm: 1.5 },
          width: '100%',
          // Removed maxHeight and overflow constraints
          flexShrink: 0, 
        }}
      >
        {letters.map((letter, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {!usedIndices.has(index) && (
              <LetterOption
                letter={letter}
                index={index}
                onSelect={() => handleLetterSelect(letter, index)}
                used={usedIndices.has(index)}
                isAnswered={isAnswered}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BuildWordGame;